import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import CategoryTag from "@/components/base/CategoryTag";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import SectionRenderer from "@/components/sections/SectionRenderer";
import ReadingProgress from "@/components/feature/ReadingProgress";
import TableOfContents from "@/components/feature/TableOfContents";
import BackToTop from "@/components/feature/BackToTop";
import { getPostBySlug, getRelatedPosts, getRelatedStoresForPost } from "@/api/content";
import { canonical, articleSchema, graphSchema, breadcrumbSchema, organizationSchema } from "@/utils/seo";
import type { Post, Section, PostCard, StoreCard } from "@/types/content";

function getShopCount(post: Post): number {
  const shopSection = post.content.find((s) => s._type === "shopReview");
  if (shopSection && "shops" in shopSection && Array.isArray((shopSection as Record<string, unknown>).shops)) {
    return ((shopSection as Record<string, unknown>).shops as unknown[]).length;
  }
  return 0;
}

export default function ArticleDetailPage({ initialData }: { initialData?: Post | null }) {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(initialData ?? null);
  const [relatedPosts, setRelatedPosts] = useState<PostCard[]>([]);
  const [relatedStores, setRelatedStores] = useState<StoreCard[]>([]);
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (!slug) return;
    getPostBySlug(slug).then((data) => {
      if (data) {
        setPost(data);
        Promise.all([
          getRelatedPosts(data.slug, 3),
          getRelatedStoresForPost(data.category, data.slug, 3),
        ]).then(([posts, stores]) => {
          setRelatedPosts(posts);
          setRelatedStores(stores);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [slug]);

  const allSections = useMemo<Section[]>(() => {
    if (!post) return [];
    const bodySections = post.content.filter((s) => s._type !== "hero");
    return [
      ...bodySections,
      ...(relatedStores.length > 0
        ? [
            {
              _type: "relatedStores" as const,
              _key: "auto-related-stores",
              title: "你可能會喜歡的探店店家",
              stores: relatedStores,
            },
          ]
        : []),
      ...(relatedPosts.length > 0
        ? [
            {
              _type: "relatedPosts" as const,
              _key: "auto-related",
              title: "你可能也喜歡",
              posts: relatedPosts,
            },
          ]
        : []),
    ];
  }, [post, relatedStores, relatedPosts]);

  const meta = useMemo(() => {
    if (!post) return null;
    const baseMeta = post.seo || {
      title: `${post.title}｜美好時光指南`,
      description: post.excerpt,
      ogImage: post.coverImage,
    };

    const articleSd = articleSchema({
      title: post.title,
      description: post.excerpt,
      path: `/articles/${post.slug}`,
      image: post.coverImage,
      publishedAt: post.publishedAt,
      authorName: post.author?.name,
      category: post.category,
      tags: post.tags,
    });

    const breadcrumbs = breadcrumbSchema([
      { name: "首頁", path: "/" },
      { name: "文章列表", path: "/articles" },
      { name: post.title, path: `/articles/${post.slug}` },
    ]);

    const org = organizationSchema();

    return {
      ...baseMeta,
      canonical: baseMeta.canonical || canonical(`/articles/${post.slug}`),
      keywords: baseMeta.keywords || [...post.tags, post.category, "探店", "評比", "美好時光指南"],
      ogType: "article",
      structuredData: graphSchema([articleSd, breadcrumbs, org]),
    };
  }, [post]);

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  if (!post || !meta) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">找不到此文章</h2>
        <p className="text-slate-400 mb-4">該文章可能不存在或已被移除</p>
        <a
          href="/articles"
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          返回文章列表
        </a>
      </div>
    );
  }

  const shopCount = getShopCount(post);

  return (
    <>
      <SEO meta={meta} />
      <ReadingProgress />

      {/* ========== Cover Hero ========== */}
      <div className="w-full relative">
        <div className="w-full h-[300px] md:h-[420px] lg:h-[480px] overflow-hidden">
          <img
            fetchpriority="high"
            loading="eager"
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-8 lg:px-12 pb-8 md:pb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <CategoryTag label={post.category} variant="dark" size="md" />
              {shopCount > 1 && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white backdrop-blur-sm">
                  合集評比（{shopCount} 間）
                </span>
              )}
            </div>
            <h1 className="text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-white leading-tight">
              {post.title}
            </h1>
          </div>
        </div>
      </div>

      {/* ========== Meta Info Bar ========== */}
      <div className="w-full border-b border-slate-100 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 md:py-5">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
            {post.author && (
              <div className="flex items-center gap-2">
                <img
                  loading="lazy"
                  src={post.author.avatar}
                  alt={post.author.name}
                  className="w-8 h-8 rounded-full object-cover ring-2 ring-slate-100"
                />
                <span className="font-medium text-slate-700">{post.author.name}</span>
              </div>
            )}
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line" /></div>
              {post.publishedAt}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line" /></div>
              {post.readingTime} 分鐘閱讀
            </span>
            {post.rating && (
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-amber-400 text-sm" />
                <span className="font-medium text-slate-700">{post.rating}</span>
                <span>({post.reviewCount} 則評價)</span>
              </span>
            )}
          </div>

          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {post.tags.map((tag) => (
                <a
                  key={tag}
                  href={`/articles?tag=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 bg-slate-50 hover:bg-primary/5 border border-slate-100 hover:border-primary/20 rounded-full text-xs text-slate-600 hover:text-primary transition-colors whitespace-nowrap"
                >
                  #{tag}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ========== Article Body ========== */}
      <div className="article-body">
        <SectionRenderer sections={allSections} />
      </div>

      <TableOfContents />
      <BackToTop />
    </>
  );
}